// src/components/super-admin/EditEnterpriseModal.js - 계정 정보 수정 모달

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faKey, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { updateEnterpriseAccount, resetEnterprisePassword } from '../../services/api';
import './EditEnterpriseModal.scss';

const AUTH_TYPE_LABELS = {
  google: 'Google SSO',
  business: '기업형',
  individual: '개인',
};

const ROLE_LABELS = {
  owner: '소유자',
  member: '멤버',
};

const EditEnterpriseModal = ({ isOpen, enterprise, onClose, onUpdated }) => {
  const [form, setForm] = useState({
    name: '',
    company_name: '',
    department: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // 비밀번호 초기화 상태
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // 계정 타입 판별
  const isEnterprise = enterprise?.account_type === 'business' || enterprise?.auth_type === 'business';

  useEffect(() => {
    if (enterprise) {
      setForm({
        name: enterprise.name || '',
        company_name: enterprise.company_name || '',
        department: enterprise.department || '',
      });
      setResetResult(null);
      setShowResetConfirm(false);
    }
  }, [enterprise]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = '이름을 입력해주세요';
    if (isEnterprise && !form.company_name.trim()) newErrors.company_name = '기업명을 입력해주세요';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !enterprise) return;
    try {
      setSaving(true);
      const submitData = { ...form };
      if (!isEnterprise) {
        delete submitData.company_name;
        delete submitData.department;
      }
      await updateEnterpriseAccount(enterprise.id, submitData);
      onUpdated();
      onClose();
    } catch (err) {
      console.error('계정 수정 실패:', err);
      alert(err.message || '계정 수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!enterprise) return;
    try {
      setResetLoading(true);
      setResetResult(null);
      const data = await resetEnterprisePassword(enterprise.id);
      setResetResult(data);
      setShowResetConfirm(false);
    } catch (err) {
      console.error('비밀번호 초기화 실패:', err);
      alert(err.message || '비밀번호 초기화에 실패했습니다.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setResetResult(null);
    setShowResetConfirm(false);
    onClose();
  };

  if (!isOpen || !enterprise) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-enterprise-modal" onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="modal-header">
          <h3>계정 정보 수정</h3>
          <button className="modal-close-btn" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* 바디 */}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* 계정 정보 요약 */}
            <div className="form-section">
              <div className="form-section-title">계정 정보</div>
              <div className="account-info-summary">
                <div className="info-row">
                  <span className="info-label">이메일</span>
                  <span className="info-value">{enterprise.email || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">가입형태</span>
                  <span className="info-value">
                    <span className={`sa-badge ${isEnterprise ? 'sa-badge-enterprise-plus' : 'sa-badge-starter'}`}>
                      {AUTH_TYPE_LABELS[enterprise.auth_type] || enterprise.auth_type || '-'}
                    </span>
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">역할</span>
                  <span className="info-value">
                    {ROLE_LABELS[enterprise.company_role] || ROLE_LABELS[enterprise.role] || (enterprise.is_owner ? '소유자' : '멤버')}
                  </span>
                </div>
              </div>
            </div>

            {/* 수정 가능 필드 */}
            <div className="form-section">
              <div className="form-section-title">수정 정보</div>
              <div className="form-group">
                <label className="form-label">이름</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="이름 입력"
                />
                {errors.name && <div className="form-error">{errors.name}</div>}
              </div>
              {isEnterprise ? (
                <div className="form-group">
                  <label className="form-label">기업명</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="기업명 입력"
                  />
                  {errors.company_name && <div className="form-error">{errors.company_name}</div>}
                  <div className="form-hint">기업명 변경은 같은 회사에 연결된 사용자에게 함께 표시될 수 있습니다.</div>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">기업명</label>
                  <input
                    type="text"
                    className="form-input"
                    value="-"
                    readOnly
                    style={{ background: '#f3f4f6', color: '#9ca3af' }}
                  />
                  <div className="form-hint">Google SSO / 개인 계정은 기업명이 없습니다</div>
                </div>
              )}
              {isEnterprise && (
                <div className="form-group">
                  <label className="form-label">소속 부서</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    placeholder="소속 부서 입력"
                  />
                </div>
              )}
            </div>

            {/* 비밀번호 초기화 */}
            <div className="form-section">
              <div className="form-section-title">
                비밀번호 초기화
                <FontAwesomeIcon icon={faKey} style={{ color: '#9ca3af', fontSize: 14 }} />
              </div>
              <div className="password-reset-section">
                <div className="reset-title">⚠️ 비밀번호 초기화</div>
                <div className="reset-description">
                  이 계정의 비밀번호를 <strong>0000</strong>으로 초기화합니다.
                  초기화 후 다음 로그인 시 비밀번호를 변경해야 합니다.
                </div>

                {/* 확인 모달 */}
                {showResetConfirm ? (
                  <div className="reset-confirm-box">
                    <div className="reset-confirm-icon">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="reset-confirm-text">
                      정말로 비밀번호를 초기화하시겠습니까?
                    </div>
                    <div className="reset-confirm-sub">
                      비밀번호가 0000으로 설정됩니다.
                    </div>
                    <div className="reset-confirm-actions">
                      <button
                        className="sa-btn sa-btn-secondary sa-btn-sm"
                        onClick={() => setShowResetConfirm(false)}
                        disabled={resetLoading}
                      >
                        취소
                      </button>
                      <button
                        className="sa-btn sa-btn-danger sa-btn-sm"
                        onClick={handleResetPassword}
                        disabled={resetLoading}
                      >
                        {resetLoading ? '초기화 중...' : '초기화 실행'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="reset-btn"
                    onClick={() => setShowResetConfirm(true)}
                  >
                    비밀번호 초기화
                  </button>
                )}

                {resetResult && (
                  <div className="reset-result">
                    <div>✅ 비밀번호가 <strong>0000</strong>으로 초기화되었습니다.</div>
                    <div className="copy-hint">오너에게 비밀번호가 초기화되었음을 알려주세요.</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 푸터 */}
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={handleClose}>취소</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEnterpriseModal;
